'use client';

import { forwardRef } from 'react';
import { cn } from '@/core/utils/cn';

interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
  description?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, label, description, id, ...props }, ref) => {
    return (
      <label
        htmlFor={id}
        className={cn(
          'flex items-start gap-2.5 cursor-pointer group select-none',
          props.disabled && 'opacity-50 cursor-not-allowed',
          className
        )}
      >
        <div className="relative mt-0.5 flex-shrink-0">
          {/* Native input — hidden but accessible */}
          <input
            ref={ref}
            id={id}
            type="checkbox"
            className="peer sr-only"
            {...props}
          />
          {/* Visual box */}
          <div
            className={cn(
              'w-[18px] h-[18px] rounded-[5px] border-2 transition-all duration-200',
              'border-hms-border bg-hms-bg/50',
              'peer-checked:bg-gradient-to-br peer-checked:from-hms-gold peer-checked:to-hms-gold/80 peer-checked:border-hms-gold',
              'peer-checked:shadow-[0_0_8px_rgba(200,145,58,0.25)]',
              'group-hover:border-hms-gold/50',
              'peer-focus-visible:ring-2 peer-focus-visible:ring-hms-gold/30 peer-focus-visible:ring-offset-1 peer-focus-visible:ring-offset-hms-bg',
            )}
          />
          {/* Check SVG — sibling of peer for peer-checked: to work */}
          <svg
            className={cn(
              'absolute top-[3px] left-[3px] w-3 h-3 pointer-events-none',
              'text-hms-bg opacity-0 peer-checked:opacity-100',
              'transition-all duration-200 peer-checked:scale-100 scale-75',
            )}
            viewBox="0 0 12 12"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M2.5 6L5 8.5L9.5 3.5" />
          </svg>
        </div>
        {(label || description) && (
          <div className="min-w-0">
            {label && (
              <span className="text-[13px] text-hms-text group-hover:text-hms-text/90 transition-colors leading-tight">
                {label}
              </span>
            )}
            {description && (
              <p className="text-[11px] text-hms-textDim mt-0.5 leading-snug">{description}</p>
            )}
          </div>
        )}
      </label>
    );
  }
);
Checkbox.displayName = 'Checkbox';
